import { Box } from '@mantine/core';

import { clamp, Easing } from '@/lib/anim/easing';

import { FRAGMENTS, T } from './storyboard';

// Ch. 01 - the problem: outreach-template spam drifting upward behind the
// opening headline. Each scene owns its own visibility window and returns null
// outside it, so the overlay can mount all six unconditionally.
export function Scene1Noise({ time }: { time: number }) {
  const [start, end] = T.noise;
  if (time < start || time > end) return null;
  const localTime = time - start;

  return (
    <Box style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
      {/* Spam fragments drifting upward */}
      {FRAGMENTS.map((f) => {
        const inFade = clamp((localTime - f.delay) / 0.4, 0, 1);
        const outFade = 1 - clamp((localTime - 2.8) / 0.7, 0, 1);
        const alpha = inFade * outFade;
        if (alpha <= 0) return null;
        const y = f.y - localTime * f.drift;
        return (
          <div
            key={f.id}
            style={{
              position: 'absolute',
              left: f.x,
              top: y,
              opacity: f.opacity * alpha,
              fontFamily: 'var(--font-mono, "DM Mono", monospace)',
              fontSize: f.size,
              color: 'var(--assay-text-label)',
              transform: `rotate(${f.rot}deg)`,
              whiteSpace: 'nowrap',
              letterSpacing: '0.04em',
              textShadow: '0 0 16px rgba(0,0,0,0.6)',
            }}
          >
            {f.text}
          </div>
        );
      })}

      <Headline localTime={localTime} />
      <Subline localTime={localTime} />
    </Box>
  );
}

function Headline({ localTime }: { localTime: number }) {
  const inT = Easing.easeOutCubic(clamp((localTime - 1) / 0.7, 0, 1));
  const outT = clamp((localTime - 2.95) / 0.55, 0, 1);
  const opacity = inT * (1 - outT);
  const ty = (1 - inT) * 24 - outT * 6;
  const underline = clamp((localTime - 2.05) / 0.7, 0, 1);
  const underlineFade = 1 - clamp((localTime - 3.05) / 0.35, 0, 1);

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '42%',
        transform: `translate(-50%, calc(-50% + ${ty}px))`,
        opacity,
        width: 1320,
        textAlign: 'center',
        fontFamily: '"Cormorant Garamond", "Georgia", serif',
        fontWeight: 300,
        fontSize: 82,
        lineHeight: 1.08,
        color: 'var(--assay-text-primary)',
        letterSpacing: '-0.025em',
        zIndex: 2,
      }}
    >
      Most tools are built for{' '}
      <span style={{ position: 'relative', whiteSpace: 'nowrap' }}>
        <span style={{ fontStyle: 'italic', color: 'var(--assay-ore-light)' }}>
          reaching strangers
        </span>
        <span
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -14,
            height: 2,
            background: 'var(--assay-ore)',
            transformOrigin: 'left',
            transform: `scaleX(${underline})`,
            opacity: underlineFade,
            boxShadow: '0 0 14px rgba(196,152,58,0.55)',
          }}
        />
      </span>
      .
    </div>
  );
}

function Subline({ localTime }: { localTime: number }) {
  const opacity =
    clamp((localTime - 1.8) / 0.6, 0, 1) * (1 - clamp((localTime - 2.95) / 0.55, 0, 1));
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '64%',
        transform: 'translate(-50%, -50%)',
        opacity,
        fontFamily: '"DM Mono", monospace',
        fontSize: 13,
        letterSpacing: '0.32em',
        textTransform: 'uppercase',
        color: 'var(--assay-text-label)',
        zIndex: 2,
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 36,
          height: 1,
          background: 'var(--assay-ore)',
          verticalAlign: 'middle',
          marginRight: 18,
        }}
      />
      A simple frustration
      <span
        style={{
          display: 'inline-block',
          width: 36,
          height: 1,
          background: 'var(--assay-ore)',
          verticalAlign: 'middle',
          marginLeft: 18,
        }}
      />
    </div>
  );
}
